import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faEllipsisVertical, faFilter } from '@fortawesome/pro-solid-svg-icons';
import {
	SHARED_FILTER_REGISTRY,
	validateActiveFilters,
} from '@supply-mover-2-0/core';
import type { HTMLAttributes, ReactNode } from 'react';
import {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Icon } from '../../../components/base/Icon';
import { FilterGroup } from '../../../components/filters/FilterGroup';
import { Container } from '../../../components/layout/Container';
import { Dropdown } from '../../../components/overlays/Dropdown';
import { Sidebar } from '../../../components/overlays/Sidebar';
import { useResponsive } from '../../../hooks/useResponsive';
import {
	HeaderFilterTray,
	useThemeColors,
	useTranslation,
} from '../../../index';
import { useListBuilderContext } from '../core/ListBuilderProvider';
import { InlineFiltersContext } from '../filters/InlineFiltersContext';

// Context to track grid layout mode (when true, controls should be rendered in grid)
const InlineControlsContext = createContext<boolean>(false);

export const useInlineControlsContext = () => useContext(InlineControlsContext);

export interface EmbeddedListLayoutProps
	extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
	children: ReactNode;
	showFilters?: boolean;
	filterCollapsed?: boolean;
	onFilterToggle?: () => void;
	customFilterElement?: ReactNode;
	title?: ReactNode;
	headerAction?: ReactNode;
	/** Structured header actions config - used for mobile dropdown extraction */
	headerActionsConfig?: {
		primary?: Array<{
			label: string;
			onClick: () => void;
			icon?: ReactNode | IconDefinition;
			hidden?: boolean;
		}>;
		secondary?: Array<{
			label: string;
			onClick: () => void;
			icon?: ReactNode | IconDefinition;
			hidden?: boolean;
		}>;
	};
	/** Active filters element - shown on desktop, hidden on mobile (uses badge instead) */
	activeFiltersElement?: ReactNode;
	/** Count of active filters for mobile badge */
	activeFilterCount?: number;
	/** Saved search button and dropdown element (desktop only) */
	savedSearchElement?: ReactNode;
	/** Overflow menu items for hamburger menu (Configure List, Save as New List, etc.) */
	overflowMenuItems?: Array<{
		label: string;
		onClick: () => void;
		icon?: ReactNode | IconDefinition;
		hidden?: boolean;
	}>;
	/**
	 * When true, the title/controls header bar is not rendered at all. Used when
	 * a host (e.g. a view-layout card) hoists the title + overflow into its own
	 * CardHeader and wants the list to render as just the table content.
	 */
	hideHeader?: boolean;
	/**
	 * Minimum height of the layout container. The 400px default suits
	 * detail-page cards; compact hosts (e.g. a row expansion holding a few
	 * lines, PD-15221) pass a smaller value so the panel hugs its content.
	 */
	minHeight?: number | string;
}

/**
 * Layout component for list views that are embedded within detail views.
 * Unlike StandardListLayout, this doesn't include a ComponentHeader since
 * the parent detail view already provides one.
 */
export function EmbeddedListLayout({
	children,
	showFilters = true,
	filterCollapsed = false,
	onFilterToggle,
	customFilterElement,
	title,
	headerAction: _headerAction,
	headerActionsConfig,
	activeFiltersElement,
	activeFilterCount = 0,
	savedSearchElement,
	overflowMenuItems,
	hideHeader = false,
	minHeight = 400,
	className = '',
	...htmlProps
}: EmbeddedListLayoutProps) {
	// Filter out non-DOM props that might come from parent components
	const { showTabs, showConfigButton, ...cleanHtmlProps } = htmlProps as any;
	// Make context optional - component should work without ListBuilderProvider
	let state = null;
	let additionalContext = undefined;
	let listKey: string | undefined = undefined;
	try {
		const context = useListBuilderContext();
		state = context.state;
		additionalContext = context.additionalContext;
		listKey = context.listKey;
	} catch (error) {
		// Component is used outside of ListBuilderProvider, which is fine
		state = null;
		additionalContext = undefined;
	}

	const { t } = useTranslation();
	const colors = useThemeColors();
	const { isMobile } = useResponsive();

	// Mobile filter sidebar state
	const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

	// Get enabled filters from configuration and validate them
	const activeFilters = state?.configuration?.filters?.activeFilters || [];
	let validatedFilters = validateActiveFilters(
		activeFilters,
		state?.configuration?.objects || [],
		listKey,
	);

	// Get hidden filters from state (set by role-based filter rules)
	const hiddenFilters = state?.hiddenFilters ?? {};

	// Remove filters hidden by role-based rules
	validatedFilters = validatedFilters.filter(
		(filterId) => hiddenFilters[filterId] !== true,
	);

	const hasFilters = showFilters && validatedFilters.length > 0;
	const hasCustomFilter = customFilterElement != null;
	const hasAnyFilters = hasFilters || hasCustomFilter;
	// Show filter column whenever filters exist (collapsed or expanded)
	const shouldShowFilterColumn = hasAnyFilters;
	const hasTitle = title != null;

	/*
	 * The filters live in a bar-and-tray under the header, exactly like the
	 * main lists (PD-15210, owner request 2026-08-03) — the left FilterSidebar
	 * column is gone. Same split rule as StandardListLayout: bar-eligible
	 * filters fill the observed row width, the remainder collapses into the
	 * tray behind the Filters toggle. Embedded cards are narrower, so most
	 * filter sets land mostly in the tray.
	 */
	const barRef = useRef<HTMLDivElement>(null);
	const [barWidth, setBarWidth] = useState(0);

	useEffect(() => {
		const el = barRef.current;
		if (el == null || typeof ResizeObserver === 'undefined') {
			return undefined;
		}
		const observer = new ResizeObserver((entries) => {
			const width = entries[0]?.contentRect.width;
			if (width != null) {
				setBarWidth(width);
			}
		});
		observer.observe(el);
		setBarWidth(el.getBoundingClientRect().width);
		return (): void => {
			observer.disconnect();
		};
	}, [hasAnyFilters]);

	const { inlineFilters, trayFilters } = useMemo(() => {
		// PD-15743 — `radio` renders as a dropdown, so it is bar-eligible.
		const INLINE_TYPES = new Set([
			'text',
			'multi-select',
			'single-select',
			'radio',
		]);
		const eligible = validatedFilters.filter((id) => {
			const type = (
				SHARED_FILTER_REGISTRY as Record<string, { type?: string } | undefined>
			)[id]?.type;
			return type != null && INLINE_TYPES.has(type);
		});
		const CONTROL_W = 200;
		const GAP = 8;
		// Row padding + the Filters toggle. No Save Search in embedded headers.
		const TOGGLE_RESERVE = 170;
		const capacity =
			barWidth > 0
				? Math.max(
						0,
						Math.floor((barWidth - TOGGLE_RESERVE + GAP) / (CONTROL_W + GAP)),
					)
				: 0;
		const inline = eligible.slice(0, capacity);
		const inlineSet = new Set(inline);
		return {
			inlineFilters: inline,
			trayFilters: validatedFilters.filter((id) => !inlineSet.has(id)),
		};
	}, [validatedFilters, barWidth]);

	// Extract dropdown items from headerActionsConfig for mobile ellipsis menu
	const mobileDropdownItems = useMemo(() => {
		if (!isMobile || headerActionsConfig == null) {
			return [];
		}

		const items: Array<{ label: string; onClick: () => void }> = [];

		// Extract from primary actions
		headerActionsConfig.primary
			?.filter((a) => a.hidden !== true)
			.forEach((action) => {
				items.push({ label: action.label, onClick: action.onClick });
			});

		// Extract from secondary actions
		headerActionsConfig.secondary
			?.filter((a) => a.hidden !== true)
			.forEach((action) => {
				items.push({ label: action.label, onClick: action.onClick });
			});

		return items;
	}, [isMobile, headerActionsConfig]);

	// Whether the title/controls header bar is rendered. When `hideHeader` is set
	// the host (e.g. a view-layout card) owns the title + overflow, so we drop
	// the header row entirely.
	const showHeaderBar = (hasTitle || isMobile) && !hideHeader;

	// minmax(0, 1fr), not 1fr: a bare 1fr track never shrinks below its widest
	// item's min-content, so in a narrow host (the trip builder's 360px list
	// beside the map) the filter tray widened the whole list past its column
	// and the table slid under the map. With a zero minimum the list keeps its
	// host's width and the table scrolls inside its own box.
	const gridConfig = useMemo(() => {
		if (isMobile) {
			// Mobile: simple single-column layout
			// Controls are inside the title/header bar, not a separate row
			if (!showHeaderBar) {
				return {
					gridTemplateColumns: 'minmax(0, 1fr)',
					gridTemplateRows: '1fr',
					gridTemplateAreas: '"content"',
				};
			}
			return {
				gridTemplateColumns: 'minmax(0, 1fr)',
				gridTemplateRows: 'auto 1fr',
				gridTemplateAreas: '"header" "content"',
			};
		}

		// Desktop: the filters are a full-width ROW under the header (the
		// bar-and-tray), not a left column.
		if (showHeaderBar && shouldShowFilterColumn) {
			return {
				gridTemplateColumns: 'minmax(0, 1fr)',
				gridTemplateRows: 'auto auto 1fr',
				gridTemplateAreas: '"header" "tray" "content"',
			};
		} else if (showHeaderBar && !shouldShowFilterColumn) {
			return {
				gridTemplateColumns: 'minmax(0, 1fr)',
				gridTemplateRows: 'auto 1fr',
				gridTemplateAreas: '"header" "content"',
			};
		} else if (!showHeaderBar && shouldShowFilterColumn) {
			return {
				gridTemplateColumns: 'minmax(0, 1fr)',
				gridTemplateRows: 'auto 1fr',
				gridTemplateAreas: '"tray" "content"',
			};
		} else {
			return {
				gridTemplateColumns: 'minmax(0, 1fr)',
				gridTemplateRows: '1fr',
				gridTemplateAreas: '"content"',
			};
		}
	}, [showHeaderBar, shouldShowFilterColumn, isMobile]);

	return (
		<InlineControlsContext.Provider value={true}>
			<Container
				variant="primary"
				padding="none"
				className={`flex-1 flex flex-col min-h-0 ${className}`}
				style={{ minHeight }}
				data-testid="embedded-list-layout-container"
				{...cleanHtmlProps}
			>
				{/* On a short window (`short:`) the frame and the gap between the
				    header bar, filters and table tighten (PD-16861). This layout is
				    the frame of every embedded list: the Shop grids and the lists on
				    the client, order and quote tabs. */}
				<div
					className={`flex-1 min-h-0 grid transition-[grid-template-columns] duration-200 ease-in-out ${
						hideHeader
							? ''
							: isMobile
								? 'gap-2'
								: 'p-4 gap-y-4 short:p-3 short:gap-y-2'
					}`}
					style={gridConfig}
					data-testid="embedded-list-layout-inner"
				>
					{/* Header bar - full width, contains title + controls */}
					{/* On desktop: title on left, controls on right (in single row spanning full width) */}
					{/* On mobile: title + inline controls + action buttons */}
					{showHeaderBar && (
						// The 52px floor moved from an inline style to classes so a short
						// window can lower it to 44px, still clear of the 32px controls
						// and the tabs it holds (PD-16861). An inline style would
						// outrank any class.
						<div
							className={`px-4 rounded-lg flex items-center gap-4 min-h-[52px] short:min-h-[44px] ${colors.classes.bgSecondary}`}
							style={{
								gridArea: 'header',
							}}
						>
							{/* Title - flex-shrink-0 to not compress */}
							{hasTitle && (
								<h3
									className={`text-lg font-semibold flex-shrink-0 ${colors.classes.textPrimary}`}
								>
									{typeof title === 'string' ? t(title) : title}
								</h3>
							)}

							{/* Desktop: Saved search button (next to title) */}
							{!isMobile && savedSearchElement != null && (
								<div className="flex-shrink-0">{savedSearchElement}</div>
							)}

							{/* Desktop: Inline controls slot (tabs go here via portal) */}
							{!isMobile && (
								<div
									className="flex-1 flex items-center justify-end min-w-0"
									data-testid="embedded-list-inline-controls"
								>
									{/* Portal from ListBuilderView will render tabs + controls here */}
								</div>
							)}

							{/* Desktop: Overflow menu (hamburger) - flex-shrink-0 to prevent compression */}
							{!isMobile &&
								overflowMenuItems != null &&
								overflowMenuItems.filter((i) => i.hidden !== true).length >
									0 && (
									<div className="flex-shrink-0">
										<Dropdown
											trigger={
												<button
													className={`relative flex items-center justify-center w-8 h-8 rounded-lg ${colors.classes.bgTertiary} ${colors.classes.textSecondary}`}
													aria-label={t('More actions')}
													type="button"
												>
													<Icon icon={faEllipsisVertical} size={16} />
												</button>
											}
											items={overflowMenuItems.filter((i) => i.hidden !== true)}
											align="right"
										/>
									</div>
								)}

							{/* Mobile: Inline controls slot in header bar */}
							{isMobile && (
								<div
									className="flex-1 min-w-0 overflow-x-auto"
									data-testid="embedded-list-inline-controls"
								>
									{/* Portal from ListBuilderView will render inline controls here */}
								</div>
							)}

							{/* Mobile: Right side buttons */}
							{isMobile && (
								<div className="flex items-center gap-2 flex-shrink-0">
									{/* Mobile: ellipsis dropdown for actions */}
									{mobileDropdownItems.length > 0 && (
										<Dropdown
											trigger={
												<button
													className={`relative flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 ${colors.classes.bgTertiary} ${colors.classes.textSecondary}`}
													aria-label={t('More actions')}
													type="button"
												>
													<Icon icon={faEllipsisVertical} size={16} />
												</button>
											}
											items={mobileDropdownItems}
											align="right"
										/>
									)}
									{/* Mobile filter button with badge */}
									{hasAnyFilters && (
										<button
											onClick={() => {
												setMobileFilterOpen(true);
											}}
											className={`relative flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 ${colors.classes.bgTertiary} ${colors.classes.textSecondary}`}
											aria-label={t('Filters')}
											type="button"
										>
											<Icon icon={faFilter} size={18} />
											{activeFilterCount > 0 && (
												<span className="absolute -top-1 -right-1 min-w-5 h-5 flex items-center justify-center px-1.5 text-xs font-medium text-white bg-accent rounded-full">
													{activeFilterCount}
												</span>
											)}
										</button>
									)}
								</div>
							)}
						</div>
					)}

					{/* Filter bar-and-tray — the same presentation as the main
					    lists (PD-15210): inline fields that fit, the rest behind
					    the Filters toggle in an overlay tray. */}
					{shouldShowFilterColumn && !isMobile && (
						<div ref={barRef} style={{ gridArea: 'tray' }}>
							<HeaderFilterTray
								gutter={false}
								// The custom filter (the shop pages' address picker) lives in the
								//  tray too, so the tray must open for it even when every list
								//  filter fits inline; otherwise the picker can never be reached.
								open={
									!filterCollapsed && (trayFilters.length > 0 || hasCustomFilter)
								}
								onToggle={onFilterToggle ?? (() => undefined)}
								activeCount={activeFilterCount}
								showToggle={trayFilters.length > 0 || hasCustomFilter}
								skeletonCount={trayFilters.length}
								inlineFilters={
									inlineFilters.length > 0 ? (
										<FilterGroup
											key={`bar:${inlineFilters.join(',')}`}
											filterTypes={inlineFilters}
											layout="bar"
										/>
									) : null
								}
							>
								{/* Custom filter element (like AddressSelector) */}
								{hasCustomFilter && (
									<div
										className={
											trayFilters.length > 0
												? `mb-4 pb-4 border-b ${colors.classes.borderDefault}`
												: 'mb-4'
										}
									>
										{customFilterElement}
									</div>
								)}
								{trayFilters.length > 0 && (
									<FilterGroup
										key={`tray:${trayFilters.join(',')}`}
										filterTypes={trayFilters}
										layout="tray"
									/>
								)}
							</HeaderFilterTray>
						</div>
					)}

					{/* Main Content - connected to filter sidebar */}
					<div
						className="flex flex-col min-h-0 min-w-0 rounded-lg"
						style={{ gridArea: 'content' }}
						data-testid="embedded-list-main-content"
					>
						{/* Active filters - desktop only, mobile uses badge on filter button.
						    The provider tells the chips which filters the bar already
						    shows, so nothing is listed twice. */}
						<InlineFiltersContext.Provider value={inlineFilters}>
							{!isMobile && activeFiltersElement}
							{children}
						</InlineFiltersContext.Provider>
					</div>
				</div>
			</Container>

			{/* Mobile Filter Sidebar */}
			{isMobile && hasAnyFilters && (
				<Sidebar
					isOpen={mobileFilterOpen}
					onClose={() => {
						setMobileFilterOpen(false);
					}}
					title={t('Filters')}
					width="sm"
					position="right"
				>
					<div className="p-4">
						{/* Custom filter element (like AddressSelector) */}
						{hasCustomFilter && (
							<div
								className={
									hasFilters
										? `mb-4 pb-4 border-b ${colors.classes.borderDefault}`
										: 'mb-4'
								}
							>
								{customFilterElement}
							</div>
						)}

						{/* Regular filters */}
						{hasFilters && (
							<FilterGroup filterTypes={validatedFilters} layout="sidebar" />
						)}
					</div>
				</Sidebar>
			)}
		</InlineControlsContext.Provider>
	);
}
